import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Plus, RefreshCw } from 'lucide-react';
import { DateTime } from 'luxon';
import type { HolidayCalendarDto, HolidayDto, HolidaySyncPlanDto } from '@workforce/shared';
import { useTz } from '@/lib/timezone';
import { t } from '@/lib/i18n';
import { ApiError, api } from '@/lib/api';
import { useCalendars } from '@/lib/queries';
import { country, flag, fmtDate } from '@/lib/format';
import { CountrySelect } from '@/components/CountrySelect';
import { Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBanner, Field, Input, Modal, Notice, PageHeader, Select, Spinner, Textarea, Toggle } from '@/components/ui';

export function HolidaysPage() {
  const qc = useQueryClient();
  const cals = useCalendars();
  const [selected, setSelected] = useState<string | null>(null);
  const [year, setYear] = useState(DateTime.now().year);
  const [calForm, setCalForm] = useState<null | { id?: string; name: string; countryCode: string; description: string; isActive: boolean; sourceType: 'manual' | 'egypt_presidency'; sourceUrl: string }>(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [hForm, setHForm] = useState<null | { id?: string; date: string; name: string; isHalfDay: boolean }>(null);
  const [del, setDel] = useState<null | { kind: 'cal'; c: HolidayCalendarDto } | { kind: 'h'; h: HolidayDto }>(null);

  useEffect(() => {
    if (!selected && cals.data?.length) setSelected(cals.data[0]!.id);
  }, [cals.data, selected]);
  const current = cals.data?.find((c) => c.id === selected);

  const holidays = useQuery({
    queryKey: ['holidays', selected, year],
    queryFn: () => api.get<HolidayDto[]>(`/holiday-calendars/${selected}/holidays?year=${year}`),
    enabled: !!selected,
  });
  const inv = () => {
    void qc.invalidateQueries({ queryKey: ['holiday-calendars'] });
    void qc.invalidateQueries({ queryKey: ['holidays'] });
  };

  const saveCal = useMutation({
    mutationFn: () => {
      const body = {
        name: calForm!.name,
        countryCode: calForm!.countryCode || null,
        description: calForm!.description,
        isActive: calForm!.isActive,
        sourceType: calForm!.sourceType,
        sourceUrl: calForm!.sourceType === 'manual' ? null : calForm!.sourceUrl || null,
      };
      return calForm!.id ? api.patch<HolidayCalendarDto>(`/holiday-calendars/${calForm!.id}`, body) : api.post<HolidayCalendarDto>('/holiday-calendars', body);
    },
    onSuccess: (c) => {
      setCalForm(null);
      setSelected(c.id);
      inv();
    },
  });
  const saveH = useMutation({
    mutationFn: () => {
      const body = { date: hForm!.date, name: hForm!.name, isHalfDay: hForm!.isHalfDay };
      return hForm!.id ? api.patch(`/holiday-calendars/${selected}/holidays/${hForm!.id}`, body) : api.post(`/holiday-calendars/${selected}/holidays`, body);
    },
    onSuccess: () => {
      setHForm(null);
      inv();
    },
  });
  const remove = useMutation({
    mutationFn: () =>
      del!.kind === 'cal' ? api.del(`/holiday-calendars/${del!.c.id}`) : api.del(`/holiday-calendars/${selected}/holidays/${del!.h.id}`),
    onSuccess: () => {
      if (del?.kind === 'cal') setSelected(null);
      setDel(null);
      inv();
    },
  });
  const calFe = saveCal.error instanceof ApiError ? saveCal.error.fieldErrors() : {};
  const hFe = saveH.error instanceof ApiError ? saveH.error.fieldErrors() : {};

  return (
    <div>
      <PageHeader
        title={t('Public holidays')}
        subtitle={t('Each employee is assigned one holiday calendar; its dates are excluded from leave calculations.')}
        actions={
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCalForm({ name: '', countryCode: '', description: '', isActive: true, sourceType: 'manual', sourceUrl: '' })}>
            {t('New calendar')}
          </Button>
        }
      />
      <ErrorBanner error={cals.error} />
      {cals.isLoading ? (
        <Spinner />
      ) : !cals.data?.length ? (
        <Card>
          <EmptyState icon={<CalendarDays className="h-6 w-6" />} title={t('No holiday calendars')} description={t('Create one per country (e.g. Lebanon, Saudi Arabia, UAE).')} />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card title={t('Calendars')} bodyClass="p-2">
            <ul className="space-y-1">
              {cals.data.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setSelected(c.id)}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-start text-sm ${selected === c.id ? 'bg-brand-50 text-brand-800' : 'hover:bg-slate-50'}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {flag(c.countryCode)} {c.name}
                      </span>
                      <span className="text-xs text-slate-500">
                        {t('{dates} dates · {employees} employees', { dates: c.holidayCount, employees: c.employeeCount })}
                      </span>
                    </span>
                    {!c.isActive && <Badge>{t('Inactive')}</Badge>}
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          {current && (
            <Card
              className="lg:col-span-2"
              title={
                <span>
                  {current.name} <span className="font-normal text-slate-500">{current.countryCode ? `· ${country(current.countryCode)}` : ''}</span>
                </span>
              }
              actions={
                <div className="flex items-center gap-1">
                  <Select value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-28 py-1" aria-label={t('Year')}>
                    {[-1, 0, 1, 2].map((d) => {
                      const y = DateTime.now().year + d;
                      return (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      );
                    })}
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setCalForm({
                        id: current.id,
                        name: current.name,
                        countryCode: current.countryCode ?? '',
                        description: current.description ?? '',
                        isActive: current.isActive,
                        sourceType: current.sourceType,
                        sourceUrl: current.sourceUrl ?? '',
                      })
                    }
                  >
                    {t('Edit')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDel({ kind: 'cal', c: current })}>
                    {t('Delete')}
                  </Button>
                </div>
              }
              bodyClass="p-0"
            >
              {current.sourceType !== 'manual' && <OfficialSourcePanel cal={current} onCheck={() => setSyncOpen(true)} />}
              {current.description && (
                <div className="border-b border-slate-100 px-5 py-3">
                  <Notice tone="amber">{current.description}</Notice>
                </div>
              )}
              <div className="flex justify-end px-5 pt-3">
                <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setHForm({ date: `${year}-01-01`, name: '', isHalfDay: false })}>
                  {t('Add holiday')}
                </Button>
              </div>
              {holidays.isLoading ? (
                <Spinner />
              ) : !holidays.data?.length ? (
                <EmptyState title={t('No holidays in {year}', { year })} />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {holidays.data.map((h) => (
                    <li key={h.id} className="flex items-center justify-between gap-3 px-5 py-2.5">
                      <div className="flex items-center gap-4">
                        <span className="w-28 text-sm font-medium tabular-nums text-slate-700">{fmtDate(h.date, 'ccc dd LLL')}</span>
                        <span className="text-sm text-slate-900">{h.name}</span>
                        {h.isHalfDay && <Badge tone="amber">{t('Half day')}</Badge>}
                        {h.source === 'official' && <Badge tone="green">{t('Official')}</Badge>}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setHForm({ id: h.id, date: h.date, name: h.name, isHalfDay: h.isHalfDay })}>
                          {t('Edit')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDel({ kind: 'h', h })}>
                          {t('Remove')}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>
      )}

      <Modal
        open={!!calForm}
        onClose={() => setCalForm(null)}
        title={calForm?.id ? t('Edit calendar') : t('New holiday calendar')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCalForm(null)}>
              {t('Cancel')}
            </Button>
            <Button loading={saveCal.isPending} disabled={!calForm?.name.trim()} onClick={() => saveCal.mutate()}>
              {t('Save')}
            </Button>
          </>
        }
      >
        {calForm && (
          <div className="space-y-4">
            <ErrorBanner error={saveCal.error && !Object.keys(calFe).length ? saveCal.error : null} />
            <Field label={t('Name')} required error={calFe.name}>
              <Input value={calForm.name} onChange={(e) => setCalForm({ ...calForm, name: e.target.value })} placeholder={t('e.g. Lebanon')} />
            </Field>
            <Field label={t('Country')} error={calFe.countryCode}>
              <CountrySelect value={calForm.countryCode} onChange={(v) => setCalForm({ ...calForm, countryCode: v })} allowEmpty emptyLabel={t('Not country-specific')} />
            </Field>
            <Field label={t('Notes')}>
              <Textarea rows={2} value={calForm.description} onChange={(e) => setCalForm({ ...calForm, description: e.target.value })} />
            </Field>
            <Field label={t('Official source')} hint={t("Linked calendars are kept in sync with the government's published list.")}>
              <Select
                value={calForm.sourceType}
                onChange={(e) => setCalForm({ ...calForm, sourceType: e.target.value as 'manual' | 'egypt_presidency' })}
              >
                <option value="manual">{t('None — I enter holidays myself')}</option>
                <option value="egypt_presidency">{t('Egyptian Presidency ({site})', { site: 'presidency.eg' })}</option>
              </Select>
            </Field>
            {calForm.sourceType !== 'manual' && (
              <Field label={t('Official page address')} error={calFe.sourceUrl} hint={t('Leave empty to use the official English holidays page.')}>
                <Input value={calForm.sourceUrl} onChange={(e) => setCalForm({ ...calForm, sourceUrl: e.target.value })} placeholder="https://www.presidency.eg/en/…" />
              </Field>
            )}
            {calForm.id && <Toggle checked={calForm.isActive} onChange={(v) => setCalForm({ ...calForm, isActive: v })} label={t('Active')} />}
          </div>
        )}
      </Modal>
      <Modal
        open={!!hForm}
        onClose={() => setHForm(null)}
        title={hForm?.id ? t('Edit holiday') : t('Add holiday')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setHForm(null)}>
              {t('Cancel')}
            </Button>
            <Button loading={saveH.isPending} disabled={!hForm?.name.trim() || !hForm?.date} onClick={() => saveH.mutate()}>
              {t('Save')}
            </Button>
          </>
        }
      >
        {hForm && (
          <div className="space-y-4">
            <ErrorBanner error={saveH.error && !Object.keys(hFe).length ? saveH.error : null} />
            <Field label={t('Date')} required error={hFe.date}>
              <Input type="date" value={hForm.date} onChange={(e) => setHForm({ ...hForm, date: e.target.value })} />
            </Field>
            <Field label={t('Name')} required error={hFe.name}>
              <Input value={hForm.name} onChange={(e) => setHForm({ ...hForm, name: e.target.value })} placeholder={t('e.g. Eid al-Fitr')} />
            </Field>
            <Toggle checked={hForm.isHalfDay} onChange={(v) => setHForm({ ...hForm, isHalfDay: v })} label={t('Half day')} description={t('Counts as half a day off in leave calculations.')} />
          </div>
        )}
      </Modal>
      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        title={del?.kind === 'cal' ? t('Delete {name}?', { name: del.c.name }) : t('Remove {name}?', { name: del?.kind === 'h' ? del.h.name : '' })}
        message={
          del?.kind === 'cal'
            ? t('Calendars that are or were assigned to employees are kept for history and can only be deactivated.')
            : t('Removing a holiday affects leave calculations for dates that have not been finalized.')
        }
        confirmLabel={del?.kind === 'cal' ? t('Delete') : t('Remove')}
        danger
        loading={remove.isPending}
        error={remove.error}
        onConfirm={() => remove.mutate()}
      />
      {current && syncOpen && <SyncDialog cal={current} onClose={() => setSyncOpen(false)} onApplied={inv} />}
    </div>
  );
}

function OfficialSourcePanel({ cal, onCheck }: { cal: HolidayCalendarDto; onCheck: () => void }) {
  const { fmt } = useTz();
  // One sentence with the link in the middle, so Arabic word order stays natural.
  const [before, after] = t('Linked to the official {link}').split('{link}');
  const tone = cal.lastSyncStatus === 'error' ? 'text-red-700' : cal.lastSyncStatus === 'changes_pending' ? 'text-amber-700' : 'text-emerald-700';
  return (
    <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 text-sm">
        <p className="font-medium text-slate-800">
          <RefreshCw className="me-1 inline h-4 w-4 text-slate-400" />
          {before}
          <a href={cal.sourceUrl ?? '#'} target="_blank" rel="noreferrer noopener" className="text-brand-700 hover:underline">
            {t('government holiday page')}
          </a>
          {after}
        </p>
        <p className={`mt-0.5 text-xs ${cal.lastSyncStatus ? tone : 'text-slate-500'}`}>
          {cal.lastSyncedAt ? t('Last checked {time} — {message}', { time: fmt(cal.lastSyncedAt, null), message: cal.lastSyncMessage ? t(cal.lastSyncMessage) : '' }) : t('Not checked yet.')}
        </p>
      </div>
      <Button size="sm" variant="secondary" className="shrink-0 whitespace-nowrap" icon={<RefreshCw className="h-4 w-4" />} onClick={onCheck}>
        {t('Check official site')}
      </Button>
    </div>
  );
}

function SyncDialog({ cal, onClose, onApplied }: { cal: HolidayCalendarDto; onClose: () => void; onApplied: () => void }) {
  const [text, setText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const preview = useMutation({
    mutationFn: (txt?: string) => api.post<HolidaySyncPlanDto>(`/holiday-calendars/${cal.id}/sync/preview`, txt ? { text: txt } : {}),
    onError: () => setShowPaste(true),
    onSettled: () => onApplied(),
  });
  const apply = useMutation({
    mutationFn: () => api.post<HolidaySyncPlanDto>(`/holiday-calendars/${cal.id}/sync`, text.trim() ? { text } : {}),
    onSuccess: () => {
      onApplied();
      onClose();
    },
  });
  useEffect(() => {
    preview.mutate(undefined);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const p = preview.data;
  const changes = p ? p.toAdd.length + p.toRemove.length : 0;
  return (
    <Modal
      open
      onClose={onClose}
      title={t('Official holidays — {name}', { name: cal.name })}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Close')}
          </Button>
          {p && changes > 0 && (
            <Button loading={apply.isPending} onClick={() => apply.mutate()}>
              {changes > 1 ? t('Apply {n} changes', { n: changes }) : t('Apply {n} change', { n: changes })}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {preview.isPending && <Spinner label={t('Reading the official page…')} />}
        <ErrorBanner error={preview.error ?? apply.error} />
        {p && (
          <>
            {changes === 0 ? (
              <Notice tone="green">{t('Up to date — all {n} official dates for {year} are already in this calendar.', { n: p.unchanged, year: p.year })}</Notice>
            ) : (
              <Notice>
                {t('The official {year} list differs from this calendar. Review the changes below, then apply. Holidays you added yourself are never changed.', { year: p.year })}
              </Notice>
            )}
            {p.warnings.map((w, i) => (
              <Notice key={i} tone="amber">
                {t(w)}
              </Notice>
            ))}
            {p.toAdd.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-semibold text-emerald-700">{t('To add ({n})', { n: p.toAdd.length })}</p>
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {p.toAdd.map((h) => (
                    <li key={h.date + h.name} className="flex gap-4 px-3 py-1.5 text-sm">
                      <span className="w-32 tabular-nums text-slate-600">{fmtDate(h.date, 'ccc dd LLL yyyy')}</span>
                      <span className="text-slate-900">{h.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {p.toRemove.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-semibold text-red-700">{t('To remove — no longer on the official list ({n})', { n: p.toRemove.length })}</p>
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {p.toRemove.map((h) => (
                    <li key={h.id} className="flex gap-4 px-3 py-1.5 text-sm">
                      <span className="w-32 tabular-nums text-slate-600">{fmtDate(h.date, 'ccc dd LLL yyyy')}</span>
                      <span className="text-slate-900 line-through decoration-red-300">{h.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {p.manualOverlaps.length > 0 && (
              <p className="text-xs text-slate-500">
                {t('Your own entries on the same dates are kept: {list}.', { list: p.manualOverlaps.map((m) => `${fmtDate(m.date, 'dd LLL')} ${m.name}`).join(', ') })}
              </p>
            )}
          </>
        )}
        {!showPaste ? (
          <button className="text-xs font-medium text-brand-700 hover:underline" onClick={() => setShowPaste(true)}>
            {t("Server can't reach the site? Paste the page text instead")}
          </button>
        ) : (
          <div className="space-y-2">
            <Field label={t('Paste the official page')} hint={t('Open the official page in your browser, select all (Ctrl+A), copy (Ctrl+C) and paste here.')}>
              <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder="Wednesday 07 January&#10;Coptic Christmas Day&#10;…" />
            </Field>
            <Button size="sm" variant="secondary" disabled={!text.trim()} loading={preview.isPending} onClick={() => preview.mutate(text)}>
              {t('Read pasted text')}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
